import { forwardRef } from 'react';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentPropsWithoutRef,
  ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { Spinner } from './feedback';

/* ────────────────────────────────────────────────────────────────────────────
   Buttons.

   `primary` is the gold bull-sheen — the one call to action per view. `floor`
   is the iris sheen and is reserved for floor semantics (redeem at floor).
   Loading swaps the label for a spinner *in place*, so the button never
   changes width mid-transaction.
   ──────────────────────────────────────────────────────────────────────────── */

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'floor';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ButtonBaseProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  children?: ReactNode;
}

const BASE =
  'relative inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium leading-none transition-all duration-150 ease-swift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas disabled:pointer-events-none disabled:opacity-45 disabled:shadow-none aria-disabled:pointer-events-none aria-disabled:opacity-45';

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 gap-1 rounded px-2.5 text-2xs',
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-5 text-[0.9375rem]',
};

/** px for the inline spinner + icon slot at each size. */
const SIZE_ICON: Record<ButtonSize, number> = { xs: 13, sm: 14, md: 16, lg: 18 };

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-bull-sheen text-[rgb(var(--on-bull))] shadow-card hover:shadow-glow-bull hover:brightness-[1.06] active:translate-y-px active:brightness-100',
  floor:
    'bg-floor-sheen text-[rgb(var(--on-bull))] shadow-card hover:shadow-glow-floor hover:brightness-[1.08] active:translate-y-px active:brightness-100',
  secondary:
    'bg-elevated text-fg ring-1 ring-inset ring-line hover:bg-overlay hover:ring-line-strong active:translate-y-px',
  outline:
    'border border-line-strong bg-transparent text-fg hover:border-line-strong hover:bg-elevated active:translate-y-px',
  ghost: 'bg-transparent text-muted hover:bg-elevated hover:text-fg active:translate-y-px',
  danger:
    'bg-down text-[rgb(var(--on-bull))] shadow-card hover:brightness-110 active:translate-y-px active:brightness-100',
};

function buttonClass({
  variant = 'primary',
  size = 'md',
  fullWidth,
  className,
}: Pick<ButtonBaseProps, 'variant' | 'size' | 'fullWidth' | 'className'>) {
  return cn(BASE, SIZES[size], VARIANTS[variant], fullWidth && 'w-full', className);
}

/**
 * Label row. Kept mounted (just `invisible`) while loading so the button holds
 * its intrinsic width and nothing around it reflows.
 */
function Content({
  loading,
  size,
  iconLeft,
  iconRight,
  children,
}: Required<Pick<ButtonBaseProps, 'size'>> &
  Pick<ButtonBaseProps, 'loading' | 'iconLeft' | 'iconRight' | 'children'>) {
  return (
    <>
      <span
        className={cn(
          'inline-flex items-center gap-1.5',
          size === 'xs' && 'gap-1',
          loading && 'invisible',
        )}
      >
        {iconLeft ? <span className="inline-flex shrink-0">{iconLeft}</span> : null}
        {children}
        {iconRight ? <span className="inline-flex shrink-0">{iconRight}</span> : null}
      </span>
      {loading ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size={SIZE_ICON[size]} />
        </span>
      ) : null}
    </>
  );
}

export interface ButtonProps
  extends ButtonBaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth,
    iconLeft,
    iconRight,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={buttonClass({ variant, size, fullWidth, className })}
      {...rest}
    >
      <Content loading={loading} size={size} iconLeft={iconLeft} iconRight={iconRight}>
        {children}
      </Content>
    </button>
  );
});

export interface LinkButtonProps
  extends ButtonBaseProps,
    Omit<ComponentPropsWithoutRef<typeof Link>, 'className' | 'children' | 'to'> {
  to: string;
}

/** Internal navigation that looks like a button. */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    fullWidth,
    iconLeft,
    iconRight,
    className,
    children,
    to,
    ...rest
  },
  ref,
) {
  return (
    <Link
      ref={ref}
      to={to}
      aria-busy={loading || undefined}
      className={buttonClass({ variant, size, fullWidth, className })}
      {...rest}
    >
      <Content loading={loading} size={size} iconLeft={iconLeft} iconRight={iconRight}>
        {children}
      </Content>
    </Link>
  );
});

export interface AnchorButtonProps
  extends ButtonBaseProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> {
  href: string;
}

/** External navigation. Always opens in a new tab, never leaks the referrer. */
export const AnchorButton = forwardRef<HTMLAnchorElement, AnchorButtonProps>(function AnchorButton(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    fullWidth,
    iconLeft,
    iconRight,
    className,
    children,
    href,
    ...rest
  },
  ref,
) {
  return (
    <a
      ref={ref}
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-busy={loading || undefined}
      className={buttonClass({ variant, size, fullWidth, className })}
      {...rest}
    >
      <Content loading={loading} size={size} iconLeft={iconLeft} iconRight={iconRight}>
        {children}
      </Content>
    </a>
  );
});

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'ghost' | 'outline' | 'solid';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  /** Required: becomes the `aria-label` for this icon-only control. */
  label: string;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
}

const ICON_SIZES: Record<IconButtonSize, string> = {
  sm: 'h-7 w-7 rounded',
  md: 'h-9 w-9 rounded-md',
  lg: 'h-11 w-11 rounded-lg',
};

const ICON_VARIANTS: Record<IconButtonVariant, string> = {
  ghost: 'bg-transparent text-muted hover:bg-elevated hover:text-fg',
  outline: 'border border-line bg-surface text-muted hover:border-line-strong hover:text-fg',
  solid: 'bg-elevated text-fg ring-1 ring-inset ring-line hover:bg-overlay hover:ring-line-strong',
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    size = 'md',
    variant = 'ghost',
    loading = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center transition-all duration-150 ease-swift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bull/70 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas active:translate-y-px disabled:pointer-events-none disabled:opacity-45',
        ICON_SIZES[size],
        ICON_VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={size === 'sm' ? 13 : size === 'lg' ? 18 : 16} /> : children}
    </button>
  );
});
